import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Moment from "react-moment";
import PrintPurchaseOrder from "../components/PrintPurchaseOrder";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePurchaseOrder,
  getPODeleteStatus,
  getPOSendStatus,
  getPoEsignPrepToken,
  getPoEsignUploadStatus,
  getPoUploadedDocUrl,
  getPurchaseOrderDoc,
  getPurchaseOrders,
  loadPurchaseOrders,
  loadUploadedPurchaseOrderDoc,
  purchaseOrderDocReceived,
  purchaseOrderEsignUploadReset,
  purchaseOrderUploadedDocReceived,
  sendPurchaseOrder,
  uploadPurchaseOrderToEsign,
  loadPoEsignLinks,
  getPoEsignLinks,
} from "../app/store/purchaseOrder";
import { getAlert, getConfirmAlert, getSigningModeChoice } from "../services/appAlerts";
import {
  getCurrentUser,
} from "../app/store/auth";
import config from "../config.json";

const PurchaseOrderPopup = (props) => {
  const {
    candidateObj,
    handlePurchaseShowHide,
    purchaseShowHide,
    workflowTask,
    poOrderType,
    item,
    handleSignUnsignPo,
    handleRefreshPoOrders,
    renderType,
    onBack,
    hideHeader,
    initialPoType = "",
    autoGenerate = false,
    autoAction = "",
    embeddedGenerator = false,
    onEmbeddedClose,
    autoSave = false,
    deferSave = false,
    initialGeneratedData = null,
    onStageGenerated = null
  } = props;
  const requestedAction = autoAction || (autoGenerate ? "generate" : "");

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [allPoTypes, setAllPoTypes] = useState([]);
  const [genShowHide, setGenShowHide] = useState(Boolean(requestedAction));
  const [poAction, setPoAction] = useState(requestedAction);
  const [poEsignLoading, setPoEsignLoading] = useState(false);
  const [loadingPoId, setLoadingPoId] = useState(null);
  const [sendLoadingPoId, setSendLoadingPoId] = useState(null);
  const [poSignedDocLoadingId, setPoSignedDocLoadingId] = useState(null);
  const [isLoadingPO, setIsLoadingPO] = useState(false);
  const [lastLoadedPerformerId, setLastLoadedPerformerId] = useState(null);

  const purchaseOrdersSelector = useSelector(getPurchaseOrders);
  const purchaseOrderDocSelector = useSelector(getPurchaseOrderDoc);
  const poSendStatusSelector = useSelector(getPOSendStatus);
  const pODeleteStatusSelector = useSelector(getPODeleteStatus);
  const poUploadedDocUrlSelector = useSelector(getPoUploadedDocUrl);
  const poEsignPrepTokenSelector = useSelector(getPoEsignPrepToken);
  const poEsignUploadStatusSelector = useSelector(getPoEsignUploadStatus);
  const poEsignLinks = useSelector(getPoEsignLinks);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  useEffect(() => {
    if (requestedAction) {
      setPoAction(requestedAction);
      setGenShowHide(true);
    }
  }, [requestedAction]);

  const getEsignBaseUrl = () =>
    window.location.hostname === "localhost"
      ? "http://localhost:5173"
      : (process?.env?.REACT_APP_ESIGN_BASEURL || "http://localhost:5173");

  const getEsignApiBaseUrl = () =>
    process?.env?.REACT_APP_ESIGN_API_BASEURL ||
    process?.env?.REACT_APP_ESIGN_API_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:5005" : getEsignBaseUrl());

  const getWorkbenchApiBaseUrl = () => (process?.env?.REACT_APP_API_BASEURL || "").replace(/\/$/, "");

  const getPoEsignValue = (poOrder, key) =>
    poOrder?.[key] ?? poOrder?.[`e${key.substring(1)}`] ?? poOrder?.[`esign${key.replace(/^ESign/, "")}`];

  const getPoEsignDocumentId = (poOrder) =>
    getPoEsignValue(poOrder, "ESignDocumentId") || poOrder?.eSignDocumentId || poOrder?.esignDocumentId;

  const getPoEsignPreparationToken = (poOrder) =>
    getPoEsignValue(poOrder, "ESignPreparationToken") || poOrder?.eSignPreparationToken || poOrder?.esignPreparationToken;

  const getPoEsignStatus = (poOrder) =>
    getPoEsignValue(poOrder, "ESignStatus") || poOrder?.eSignStatus || poOrder?.esignStatus;

  const isPoEsignSigned = (poOrder) => {
    const status = (getPoEsignStatus(poOrder) || "").replace(/\s+/g, "").toLowerCase();
    return status === "signed" || status === "completed" || !!getPoEsignValue(poOrder, "ESignCompletedDateTime");
  };

  const getPoEsignBadgeDetails = (status, documentId) => {
    if (!status && !documentId) return null;
    const normalized = (status || "Draft").replace(/\s+/g, "").toLowerCase();

    switch (normalized) {
      case "signed":
      case "completed":
        return { text: "Signed", icon: "fa-check-circle", className: "done" };
      case "partiallysigned":
        return { text: "Partially Signed", icon: "fa-adjust", className: "pending" };
      case "sent":
        return { text: "Sent", icon: "fa-paper-plane-o", className: "pending" };
      case "rejected":
      case "cancelled":
      case "canceled":
      case "expired":
        return { text: status, icon: "fa-exclamation-circle", className: "danger" };
      default:
        return { text: "Draft", icon: "fa-pencil-square-o", className: "draft" };
    }
  };

  useEffect(() => {
    if (candidateObj?.performerid > 0 && lastLoadedPerformerId !== candidateObj.performerid && !isLoadingPO) {
      setIsLoadingPO(true);
      setLastLoadedPerformerId(candidateObj.performerid);
      dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
    }

    const poTypes = [];
    if (candidateObj.subcontractor?.fuid > 0) {
      poTypes.push({
        id: candidateObj?.subcontractor.fuid,
        name: "Subcontractor",
        value: "subcontractor",
      });
    }

    if (candidateObj.recruiter?.fuid > 0) {
      poTypes.push({
        id: candidateObj.recruiter.fuid,
        name: "Recruiter",
        value: "recruiter",
      });
    }

    setAllPoTypes(poTypes);
  }, [candidateObj?.performerid, candidateObj?.subcontractor, candidateObj?.recruiter, item?.partnerType, dispatch]);

  useEffect(() => {
    if (purchaseOrdersSelector && purchaseOrdersSelector.length > 0) {
      setIsLoadingPO(false);
      setPurchaseOrders(purchaseOrdersSelector);
    } else if (purchaseOrdersSelector && purchaseOrdersSelector.length === 0) {
      setIsLoadingPO(false);
      setPurchaseOrders([]);
    }
  }, [purchaseOrdersSelector]);

  // Fetch a purchase order's eSign links lazily (when its dropdown is opened) instead of
  // eagerly for every order on load. The old effect fired one slow GetPoESignLinks request
  // per order, flooding the network on mount. Every consumer already falls back to
  // env-derived URLs, so links are only needed on demand.
  const ensurePoEsignLinks = (poId) => {
    if (poId && !poEsignLinks[poId]) {
      dispatch(loadPoEsignLinks(poId));
    }
  };

  const handleGenShowHide = (action) => {
    if (action === "save") {
      setGenShowHide(false);
      setPoAction("");
      if (!autoSave) getAlert("", "Added Successfully", "success");
      if (candidateObj?.performerid > 0) {
        dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
        handleRefreshPoOrders();
      }
      if (requestedAction && onEmbeddedClose) onEmbeddedClose(true);
    } else {
      setGenShowHide(!genShowHide);
      setPoAction(action);
    }
  };

  const handleDownloadFile = async (item, type) => {
    dispatch(loadUploadedPurchaseOrderDoc(item.id, type));
  };

  const hasSignedDocumentData = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const getPurchaseOrderDocumentType = (poOrder) =>
    hasSignedDocumentData(poOrder?.ipcsSignedData) &&
      hasSignedDocumentData(poOrder?.partnerSignedData)
      ? "partner"
      : "ipcs";

  const handleOpenPoSignedDoc = (poOrder, linkType) => {
    try {
      setPoSignedDocLoadingId(poOrder.id);
      const links = poEsignLinks[poOrder.id] || {};
      const token = getPoEsignPreparationToken(poOrder);
      const apiBaseUrl = getEsignApiBaseUrl().replace(/\/$/, "");
      const fallbackUrl = token && apiBaseUrl
        ? `${apiBaseUrl}/api/public/documents/${token}/download/${linkType === "visibleSigned" ? "visible-signed" : "final-signed"}?inline=true`
        : "";
      const documentUrl = links[linkType] || fallbackUrl;

      if (documentUrl) {
        window.open(documentUrl, "_blank");
        return;
      }

      dispatch(loadUploadedPurchaseOrderDoc(poOrder.id, getPurchaseOrderDocumentType(poOrder)));
    } finally {
      setPoSignedDocLoadingId(null);
    }
  };

  const handleViewPoFile = (poOrder, type = "") => {
    const apiBaseUrl = getWorkbenchApiBaseUrl();
    const viewUrl = `${apiBaseUrl}/api/${config.GetPoOrderDocument}${poOrder.id}&type=${encodeURIComponent(type)}&inline=true`;
    window.open(viewUrl, "_blank");
  };

  useEffect(() => {
    if (purchaseOrderDocSelector?.size === 0) {
      getAlert("", "Document Not Found", "error");
      dispatch(purchaseOrderDocReceived(null));
    } else if (purchaseOrderDocSelector) {
      const url = window.URL.createObjectURL(purchaseOrderDocSelector);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      dispatch(purchaseOrderDocReceived(null));
    }
  }, [purchaseOrderDocSelector]);

  useEffect(() => {
    if (poUploadedDocUrlSelector && poUploadedDocUrlSelector?.uploadUrl) {
      window.open(poUploadedDocUrlSelector.uploadUrl, "_blank");
      dispatch(purchaseOrderUploadedDocReceived(null));
    }
  }, [poUploadedDocUrlSelector]);

  useEffect(() => {
    if (poEsignUploadStatusSelector === "success" && poEsignPrepTokenSelector && !isLoadingPO) {
      setPoEsignLoading(false);
      setLoadingPoId(null);
      window.open(`${getEsignBaseUrl()}/prepare/${poEsignPrepTokenSelector}`, "_blank");
      dispatch(purchaseOrderEsignUploadReset());
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
        if (handleRefreshPoOrders) handleRefreshPoOrders();
      }
    } else if (poEsignUploadStatusSelector === "failed") {
      setPoEsignLoading(false);
      setLoadingPoId(null);
      getAlert("", "Failed to upload purchase order to eSign API.", "error");
      dispatch(purchaseOrderEsignUploadReset());
    }
  }, [poEsignUploadStatusSelector, poEsignPrepTokenSelector, candidateObj?.performerid, dispatch, handleRefreshPoOrders]);

  const handleSendPoThroughEsign = async (poOrder) => {
    const signingMode = await getSigningModeChoice({
      message: "Choose how the signers should sign this purchase order.",
    });

    if (!signingMode) return;

    setPoEsignLoading(true);
    setLoadingPoId(poOrder.id);
    const frm = new FormData();
    frm.append("poId", poOrder.id);
    frm.append("ciid", poOrder.ciid || workflowTask?.ciid);
    frm.append("userId", currentUser?.id);
    frm.append("signingMode", signingMode);
    dispatch(uploadPurchaseOrderToEsign(frm));
  };

  const handleSendPoWithoutEsign = async (poOrder) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to send this Purchase Order without eSign?",
      "warning"
    );

    if (result.isConfirmed) {
      setSendLoadingPoId(poOrder.id);
      dispatch(sendPurchaseOrder(poOrder.id));
    }
  };

  const handleDeletePoOrder = async (id) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to Delete this Purchase Order?",
      "warning"
    );
    if (result.isConfirmed) {
      dispatch(deletePurchaseOrder(id));
    }
  };

  useEffect(() => {
    if (poSendStatusSelector && !isLoadingPO) {
      setSendLoadingPoId(null);
      getAlert("", "Purchase Order Sent Successfully", "success");
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
        if (handleRefreshPoOrders) handleRefreshPoOrders();
      }
    }
  }, [poSendStatusSelector, candidateObj?.performerid, dispatch, handleRefreshPoOrders]);

  useEffect(() => {
    if (pODeleteStatusSelector && !isLoadingPO) {
      getAlert("", "Deleted Successfully", "success");
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
      }
    }
  }, [pODeleteStatusSelector, candidateObj?.performerid, dispatch]);

  const handlePoRefresh = (type) => {
    if (candidateObj?.performerid > 0) {
      dispatch(loadPurchaseOrders(candidateObj.performerid, currentUser?.id));
      //setUpdateCompStatus(true);
    }
  };

  const poBody = () => {
    const getNormalizedPoType = (po) => String(po?.type || "other").toLowerCase();
    const poTypeOrder = { subcontractor: 0, recruiter: 1, other: 2 };
    const groupedPurchaseOrders = [...purchaseOrders].sort((a, b) =>
      (poTypeOrder[getNormalizedPoType(a)] ?? 2) - (poTypeOrder[getNormalizedPoType(b)] ?? 2)
    );

    return <div className="row">
      {poOrderType === "add" && (
        <div className="col-12 d-flex align-items-center justify-content-between mb-4">
          <div className="pop-tabs-list pop-action-tabs" role="tablist" aria-label="Purchase order actions">
            <button
              type="button"
              role="tab"
              className={`pop-tab-item ${poAction !== "upload" ? "active" : ""}`}
              onClick={() => handleGenShowHide("generate")}
            >
              <i className="fa fa-plus-circle" /> Generate PO
            </button>
            <button
              type="button"
              role="tab"
              className={`pop-tab-item ${poAction === "upload" ? "active" : ""}`}
              onClick={() => handleGenShowHide("upload")}
            >
              <i className="fa fa-cloud-upload" /> Upload PO
            </button>
          </div>
        </div>
      )}

      <div className="col-md-12">
        {groupedPurchaseOrders.length === 0 ? (
          <div className="pop-empty">
            <div className="pop-empty-icon"><i className="fa fa-inbox" /></div>
            <h5>No purchase orders</h5>
            <p>Generate or upload a purchase order to send it for eSign.</p>
          </div>
        ) : (
          <>
            <div className="pop-doc-list">
              {groupedPurchaseOrders.map((item, index) => {
                const normalizedPoType = getNormalizedPoType(item);
                const previousPoType = index > 0 ? getNormalizedPoType(groupedPurchaseOrders[index - 1]) : "";
                const showGroupHeading = index === 0 || normalizedPoType !== previousPoType;
                const groupLabel = normalizedPoType === "subcontractor"
                  ? "Subcontractor Purchase Orders"
                  : normalizedPoType === "recruiter"
                    ? "Recruiter Purchase Orders"
                    : "Other Purchase Orders";
                const groupIcon = normalizedPoType === "recruiter" ? "fa-user-plus" : "fa-paper-plane";
                const esignDocumentId = getPoEsignDocumentId(item);
                const esignPreparationToken = getPoEsignPreparationToken(item);
                const esignStatus = getPoEsignStatus(item);
                const esignBadge = getPoEsignBadgeDetails(esignStatus, esignDocumentId || esignPreparationToken);
                const esignToken = esignPreparationToken;
                const esignSigned = isPoEsignSigned(item);
                const badge = (item.partnerSign || item.partnerSignStatus === 1 || esignSigned)
                  ? { text: "Received", icon: "", className: "done" }
                  : (esignBadge || (item.sendStatus
                    ? { text: "Sent", icon: "", className: "pending" }
                    : { text: "Not Sent", icon: "", className: "draft" }));

                return (
                  <React.Fragment key={`po-${item.id}`}>
                    {showGroupHeading && (
                      <div className={`pop-section-label pop-po-group-label ${index > 0 ? "is-next-group" : ""}`}>
                        <i className={`fa ${groupIcon}`} /> {groupLabel}
                      </div>
                    )}
                    <div className="pop-doc-row">
                      <div className="pop-doc-file">
                        <span className="pop-doc-icon" aria-hidden="true">
                          <i className="fa fa-file-pdf-o" />
                        </span>
                        <div className="pop-doc-file-main">
                          <div className="pop-doc-title-line">
                            <button
                              type="button"
                              className="pop-doc-name pop-doc-name-link"
                              onClick={() => handleViewPoFile(item, "")}
                              title="Open purchase order"
                            >
                              Purchase Order #{item.id}
                            </button>
                          </div>
                          <div className="pop-doc-meta">
                            <span><Moment format="MMM Do YYYY">{item.addedDateTime}</Moment> | <Moment format="h:mm a">{item.addedDateTime}</Moment></span>
                            {item.ipcsPoName && <span>{item.ipcsPoName}</span>}
                            {(esignToken || esignDocumentId || item.sendStatus ||
                              hasSignedDocumentData(item.ipcsSignedData) ||
                              hasSignedDocumentData(item.partnerSignedData)) && (
                              <span className="pop-doc-esign-tag">
                                <i className="fa fa-pencil-square-o" /> eSign
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="pop-doc-actions-panel">
                        <span className="pop-action-slot pop-doc-status-stack">
                          <span className={`pop-status ${badge.className}`}>
                            <i className={`fa ${badge.icon}`} /> {badge.text}
                          </span>
                          {item.sentDateTime && (
                            <span className="pop-doc-status-date">
                              <Moment format="MMM Do YYYY">{item.sentDateTime}</Moment> · <Moment format="h:mm a">{item.sentDateTime}</Moment>
                            </span>
                          )}
                        </span>

                        <span className="pop-doc-actions-divider" />

                        {!item.sendStatus && !esignToken &&
                          !hasSignedDocumentData(item.ipcsSignedData) &&
                          !hasSignedDocumentData(item.partnerSignedData) ? (
                            <span className={`pop-action-slot dropdown ${index === groupedPurchaseOrders.length - 1 ? "dropup" : ""
                              }`}>
                              <button
                                className="pop-btn pop-btn-sm dropdown-toggle"
                                type="button"
                                id={`poSendDropdown-${item.id}`}
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                title="Send Options"
                                disabled={poEsignLoading || sendLoadingPoId === item.id}
                              >
                                {loadingPoId === item.id || sendLoadingPoId === item.id ? (
                                  <><i className="fa fa-spinner fa-spin" /> Sending...</>
                                ) : (
                                  <><i className="fa fa-paper-plane-o" /> Send</>
                                )}
                              </button>
                              <div className="dropdown-menu dropdown-menu-right" aria-labelledby={`poSendDropdown-${item.id}`}>
                                <button
                                  className="dropdown-item text-left"
                                  type="button"
                                  onClick={() => handleSendPoWithoutEsign(item)}
                                >
                                  <i className="fa fa-paper-plane-o mr-2" /> Send
                                </button>
                                <button
                                  className="dropdown-item text-left"
                                  type="button"
                                  onClick={() => handleSendPoThroughEsign(item)}
                                  disabled={poEsignLoading}
                                >
                                  <i className="fa fa-pencil-square-o mr-2" /> Send to eSign
                                </button>
                              </div>
                            </span>
                        ) : (
                          <span className={`pop-action-slot dropdown ${index === groupedPurchaseOrders.length - 1 ? "dropup" : ""
                            }`}>
                            <button
                              className="pop-btn pop-btn-sm dropdown-toggle"
                              type="button"
                              id={`poEsignDropdown-${item.id}`}
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                              title="eSign Options"
                              onClick={() => {
                                if (esignToken) ensurePoEsignLinks(item.id);
                              }}
                            >
                              <i className="fa fa-pencil-square-o" /> eSign
                            </button>
                            <div className="dropdown-menu dropdown-menu-right" aria-labelledby={`poEsignDropdown-${item.id}`}>
                              <button
                                className="dropdown-item text-left"
                                type="button"
                                onClick={() => handleOpenPoSignedDoc(item, "visibleSigned")}
                                disabled={poSignedDocLoadingId === item.id}
                              >
                                <i className="fa fa-file-pdf-o mr-2" /> Visible Signed
                              </button>
                              <button
                                className="dropdown-item text-left"
                                type="button"
                                onClick={() => handleOpenPoSignedDoc(item, "finalSigned")}
                                disabled={poSignedDocLoadingId === item.id}
                              >
                                {poSignedDocLoadingId === item.id ? (
                                  <><i className="fa fa-spinner fa-spin mr-2" /> Opening...</>
                                ) : (
                                  <><i className="fa fa-certificate mr-2" /> Digital Signed</>
                                )}
                              </button>
                            </div>
                          </span>
                        )}

                        <span className="pop-action-slot">
                          <button
                            className="pop-btn pop-btn-sm pop-btn-ghost-danger"
                            onClick={() => handleDeletePoOrder(item.id)}
                            title="Delete PO"
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  }

  const poHeader = () => (
    <div className="pop-header">
      <div className="rt-header-shell">
        <div className="rt-header-top">
          <div className="rt-header-main">
            <i className="rt-header-icon fa fa-file-text-o" aria-hidden="true" />
            <div className="rt-header-copy">
              <h2 className="rt-modal-title">
                <span className="rt-modal-title-text">
                  <span>Purchase Orders</span>
                  <span className="rt-modal-title-badge">Purchase Order</span>
                </span>
              </h2>
              <div className="rt-modal-meta-row">
                <span className="rt-meta-chip">
                  <i className="fa fa-info-circle" style={{ color: "#0891b2" }} />
                  <span>Manage and track purchase order documents</span>
                </span>
                {candidateObj?.performerid && (
                  <span className="rt-meta-chip">
                    <i className="fa fa-id-badge" style={{ color: "#6366f1" }} />
                    <span>ID: {candidateObj.performerid}</span>
                  </span>
                )}
                {candidateObj?.performername && (
                  <span className="rt-meta-chip">
                    <i className="fa fa-user" style={{ color: "#9333ea" }} />
                    <span>{candidateObj.performername} {candidateObj.lastName || ""}</span>
                  </span>
                )}
                {candidateObj?.performeremail && (
                  <span className="rt-meta-chip">
                    <i className="fa fa-envelope-o" style={{ color: "#0284c7" }} />
                    <span>{candidateObj.performeremail}</span>
                  </span>
                )}
                {workflowTask?.ciid && (
                  <span className="rt-meta-chip">
                    <i className="fa fa-hashtag" style={{ color: "#d97706" }} />
                    <span>CIID: {workflowTask.ciid}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="pop-header-right" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              className="pop-action-btn back"
              onClick={() => onBack ? onBack() : handlePurchaseShowHide("")}
            >
              <i className="fa fa-arrow-left" /> Back
            </button>
            <button
              type="button"
              className="pop-action-btn refresh mr-2"
              onClick={() => handlePoRefresh("")}
              title="Refresh List"
            >
              <i className="fa fa-refresh" /> Refresh
            </button>
            {renderType !== "generate" && (
              <button
                type="button"
                className="rt-action-btn"
                onClick={() => handlePurchaseShowHide("")}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <style>{`
        /* ── Scoped Style for PurchaseOrderPopup ── */
        .modal-backdrop {
          z-index: 1080 !important;
        }
        .modal {
          z-index: 1090 !important;
        }
        .modal-backdrop.show {
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
          background-color: rgba(15, 23, 42, 0.45) !important;
          opacity: 1 !important;
        }
        .modal.fade .pop-modal-xl.modal-dialog {
          transform: scale(0.95) !important;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }
        .modal.show .pop-modal-xl.modal-dialog {
          transform: scale(1) !important;
        }
        .pop-modal-xl {
          max-width: 90% !important;
          width: 1200px !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
          color: #1e293b !important;
        }
        .pop-modal-xl .modal-content {
          border-radius: 0 !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden !important;
          background: #ffffff !important;
        }

        .pop-container,
        .pop-header,
        .pop-content-body {
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .pop-container .fa,
        .pop-header .fa {
          font-family: FontAwesome;
        }

        /* ── Header Section ── */
        .pop-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          padding: 22px 28px 18px !important;
          background: #ffffff !important;
          border-bottom: 1px solid #edf2f7;
          border-radius: 0 !important;
        }
        .pop-header .rt-header-shell {
          display: flex !important;
          flex-direction: column !important;
          gap: 12px !important;
          width: 100% !important;
        }
        .pop-header .rt-header-top {
          display: flex !important;
          justify-content: space-between !important;
          align-items: flex-start !important;
          gap: 16px !important;
          width: 100% !important;
        }
        .pop-header .rt-header-main {
          display: flex !important;
          align-items: center !important;
          gap: 14px !important;
          min-width: 0 !important;
          flex: 1 1 auto !important;
        }
        .pop-header .rt-header-icon {
          width: 48px !important;
          height: 48px !important;
          min-height: 48px !important;
          border-radius: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #171717 !important;
          background: #f5f5f5 !important;
          border: 1px solid #d4d4d4 !important;
          box-shadow: 0 2px 6px -2px rgba(15,23,42,0.08) !important;
          font-size: 18px !important;
          flex-shrink: 0 !important;
        }
        .pop-header .rt-header-copy {
          display: flex !important;
          flex-direction: column !important;
          gap: 6px !important;
          min-width: 0 !important;
        }
        .pop-header .rt-modal-title {
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          color: #1e293b !important;
          font-size: 18px !important;
          font-weight: 500 !important;
          line-height: 1.2 !important;
        }
        .pop-header .rt-modal-title-text {
          display: inline-flex !important;
          align-items: center !important;
          gap: 10px !important;
          flex-wrap: wrap !important;
        }
        .pop-header .rt-modal-title-badge {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 3px 8px !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          background: #171717 !important;
          color: #ffffff !important;
          border: 1px solid #171717 !important;
        }
        .pop-header .rt-modal-meta-row {
          display: flex !important;
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
          font-size: 12px !important;
        }
        .pop-header .rt-meta-chip {
          display: inline-flex !important;
          align-items: center !important;
          gap: 4px !important;
          background: #ffffff !important;
          padding: 3px 8px !important;
          height: 24px !important;
          min-height: 24px !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          font-size: 11.5px !important;
          font-weight: 500 !important;
          color: #0f172a !important;
          border: 1px solid #cbd5e1 !important;
        }
        .pop-header .rt-action-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 38px !important;
          height: 38px !important;
          padding: 0 !important;
          font-size: 14px !important;
          color: #64748b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
          outline: none !important;
        }
        .pop-header .rt-action-btn:hover {
          color: #1e293b !important;
          border-color: #94a3b8 !important;
          background: #f8fafc !important;
          transform: translateY(-1px) !important;
        }
        .pop-header .rt-action-btn:hover i {
          color: #ef4444 !important;
        }

        .pop-container {
          width: 100%;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 0 !important;
          overflow: hidden;
          box-shadow: none;
          margin-bottom: 10px;
        }
        .pop-content-body { padding: 24px; background: #ffffff; }

        /* ── Action Buttons ── */
        .pop-action-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 8px; font-size: 13.5px; font-weight: 500; border-radius: 0 !important;
          cursor: pointer; transition: all 0.2s ease;
          padding: 7px 14px; border: 1px solid #cbd5e1; outline: none;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #1e293b; background: #ffffff;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08);
          min-height: unset; line-height: 1.25;
        }
        .pop-action-btn.generate {
          background: #2563eb; color: #ffffff; border-color: #2563eb;
          box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2);
        }
        .pop-action-btn.generate:hover {
          background: #1d4ed8; border-color: #1d4ed8; color: #ffffff;
          box-shadow: 0 3px 8px rgba(37, 99, 235, 0.28);
        }
        .pop-action-btn.upload:hover,
        .pop-action-btn.refresh:hover,
        .pop-action-btn.back:hover {
          background: #f8fafc; border-color: #94a3b8; color: #1e293b;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12);
        }

        /* Document-style PO rows */
        .pop-section-label {
          display: inline-flex; align-items: center; gap: 8px;
          margin: 0 0 12px; color: #334155; font-size: 12px;
          font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
        }
        .pop-doc-list {
          display: flex; flex-direction: column; gap: 8px;
        }
        .pop-doc-row {
          display: grid; grid-template-columns: minmax(0, 1fr) auto;
          align-items: center; gap: 12px; padding: 16px;
          background: #ffffff; border: 1px solid #cbd5e1; border-radius: 0 !important;
          box-shadow: none !important;
          transition: border-color 0.15s ease, transform 0.15s ease;
          position: relative;
        }
        .pop-doc-row:hover {
          border-color: #94a3b8;
          background: #f8fafc;
          transform: translateY(-1px);
        }
        .pop-doc-row:focus-within,
        .pop-doc-row:has(.show) {
          z-index: 10;
        }
        .pop-doc-icon {
          width: 34px; height: 34px; border-radius: 0 !important;
          display: inline-flex; align-items: center; justify-content: center;
          background: #f8fafc; color: #171717; border: 1px solid #cbd5e1;
          font-size: 15px;
        }
        .pop-doc-file {
          display: grid; grid-template-columns: 34px minmax(0, 1fr);
          align-items: center; gap: 12px; min-width: 0;
        }
        .pop-doc-file-main { min-width: 0; }
        .pop-doc-title-line {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .pop-doc-name {
          padding: 0; margin: 0;
          color: #1e293b; font-weight: 600; font-size: 13.5px;
          display: inline-flex; align-items: center; gap: 6px;
          text-align: left; line-height: 1.25;
        }
        .pop-doc-meta {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          margin-top: 4px; color: #64748b; font-size: 11.5px; font-weight: 500;
        }
        .pop-doc-meta span {
          display: inline-flex; align-items: center; gap: 5px;
          min-width: 0; max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .pop-doc-esign-tag,
        .pop-doc-type-tag {
          width: fit-content; padding: 2px 8px; border-radius: 0 !important;
          background: #f8fafc; border: 1px solid #cbd5e1;
          color: #475569; font-size: 11px; font-weight: 600;
        }
        .pop-doc-actions-panel {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 10px; align-self: center; min-width: 0;
        }
        .pop-action-slot {
          display: inline-flex; align-items: center; justify-content: flex-end; min-width: 0;
        }
        .pop-doc-status-stack {
          flex-direction: column; align-items: center; justify-content: center; gap: 4px;
        }
        .pop-doc-status-date {
          color: #64748b; font-size: 11px; font-weight: 500;
          line-height: 1.1; text-align: center; white-space: nowrap;
        }
        .pop-doc-actions-divider {
          width: 1px; height: 24px; background: #cbd5e1; margin: 0 4px;
          align-self: center;
        }
        .pop-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 7px; font-size: 13.5px; font-weight: 500; border-radius: 0 !important;
          cursor: pointer; transition: all 0.15s ease; padding: 7px 14px;
          border: 1px solid #cbd5e1; background: #ffffff; color: #1e293b;
          text-decoration: none !important; white-space: nowrap;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08);
          min-height: unset; line-height: 1.25;
        }
        .pop-btn:hover {
          background: #f8fafc; border-color: #94a3b8; color: #1e293b;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12);
        }
        .pop-btn-sm { padding: 6px 12px; font-size: 13px; font-weight: 500; }
        .pop-btn-primary {
          background: #2563eb; border-color: #2563eb; color: #ffffff;
          box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2);
        }
        .pop-btn-primary:hover {
          background: #1d4ed8; border-color: #1d4ed8; color: #ffffff;
        }
        .pop-btn-ghost-danger {
          width: auto; min-width: 34px; padding: 6px 12px; font-size: 13px; font-weight: 500;
          color: #dc2626; border-color: #cbd5e1; background: #ffffff;
        }
        .pop-btn-ghost-danger:hover {
          color: #dc2626; background: #fef2f2; border-color: #ef4444;
        }
        .pop-btn:disabled {
          opacity: 0.6; cursor: not-allowed; transform: none;
        }
        .pop-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          min-height: 180px; border: 1px dashed #cbd5e1; border-radius: 0 !important;
          background: #f8fafc; color: #64748b; text-align: center; padding: 28px;
        }
        .pop-empty-icon {
          width: 40px; height: 40px; border-radius: 0 !important; display: inline-flex;
          align-items: center; justify-content: center; background: #ffffff;
          border: 1px solid #cbd5e1; color: #171717; margin-bottom: 10px; font-size: 16px;
        }
        .pop-empty h5 { margin: 0 0 4px; color: #0f172a; font-size: 14px; font-weight: 600; }
        .pop-empty p { margin: 0; font-size: 12.5px; }

        /* Dropdown menu items */
        .pop-container .dropdown-menu {
          border-radius: 0 !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: 0 10px 25px rgba(15, 23, 42, 0.1) !important;
        }
        .pop-container .dropdown-item {
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 13px; font-weight: 500; color: #1e293b;
        }

        /* ── Table Styling ── */
        .pop-table {
          width: 100% !important; border-collapse: separate !important; border-spacing: 0 2px !important;
        }
        .pop-thead th {
          background: #f8fafc !important; color: #334155 !important; font-size: 12px !important;
          font-weight: 700 !important; text-transform: uppercase !important; letter-spacing: 0.04em !important;
          padding: 12px 16px !important; border: none !important; border-bottom: 1px solid #cbd5e1 !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
        .pop-table-row {
          background: #ffffff !important; transition: all 0.2s ease !important;
        }
        .pop-table-row:hover {
          background: #f8fafc !important;
        }
        .pop-table-row td {
          padding: 12px 16px !important; font-size: 13.5px !important; font-weight: 400 !important;
          color: #1e293b !important;
          border: none !important; border-bottom: 1px solid #e2e8f0 !important; vertical-align: middle !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }

        /* Status Badges */
        .pop-status {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 8px; border-radius: 0 !important; font-size: 11px; font-weight: 600;
          white-space: nowrap !important;
        }
        .pop-status.pending { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
        .pop-status.done { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
        .pop-status.draft { background: #f8fafc; color: #64748b; border: 1px solid #cbd5e1; }
        .pop-status.danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        
        .pop-link-btn {
          color: #2563eb; font-weight: 500; text-decoration: none !important;
          display: inline-flex; align-items: center; gap: 4px;
          transition: color 0.2s ease; cursor: pointer; border: none; background: none; padding: 0;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 13.5px;
        }
        .pop-link-btn:disabled { color: #94a3b8; cursor: not-allowed; }
        .pop-link-btn:hover { color: #1d4ed8; }
        .pop-link-btn.delete { color: #dc2626; }
        .pop-link-btn.delete:hover { color: #b91c1c; }

        .wf-modal-footer-close-btn {
          display: inline-flex !important;
          align-items: center !important;
          gap: 7px !important;
          padding: 7px 16px !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #1e293b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
          outline: none !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }
        .wf-modal-footer-close-btn:hover {
          background: #f8fafc !important;
          color: #1e293b !important;
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
        }
        .wf-modal-footer-close-btn:hover i {
          color: #ef4444 !important;
        }

        /* Document-style segmented tabs */
        .pop-tabs-list {
          display: flex !important;
          align-items: center !important;
          gap: 4px !important;
          width: fit-content !important;
          max-width: 100% !important;
          padding: 4px !important;
          margin: 0 !important;
          flex-wrap: wrap !important;
          background: #f5f5f5 !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .pop-tab-item {
          display: inline-flex !important;
          align-items: center !important;
          gap: 7px !important;
          min-height: 32px !important;
          padding: 6px 12px !important;
          border: 1px solid transparent !important;
          border-radius: 0 !important;
          background: transparent !important;
          color: #64748b !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          line-height: 1.2 !important;
          cursor: pointer !important;
          box-shadow: none !important;
          outline: none !important;
          white-space: nowrap !important;
        }
        .pop-tab-item:hover {
          color: #1e293b !important;
          background: #ffffff !important;
        }
        .pop-tab-item.active,
        .pop-tab-item.active:hover,
        .pop-tab-item.active:focus {
          color: #ffffff !important;
          background: #171717 !important;
          border-color: #171717 !important;
          box-shadow: none !important;
        }
        .pop-tab-item i {
          color: #94a3b8 !important;
          font-size: 13px !important;
        }
        .pop-tab-item.active i {
          color: #ffffff !important;
        }
        .pop-po-group-label {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          gap: 8px !important;
          margin: 0 0 6px !important;
          padding: 7px 0 !important;
          color: #334155 !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          letter-spacing: 0.04em !important;
          text-transform: uppercase !important;
        }
        .pop-po-group-label::after {
          content: "";
          flex: 1 1 auto;
          height: 1px;
          margin-left: 2px;
          background: #e2e8f0;
        }
        .pop-po-group-label.is-next-group {
          margin-top: 20px !important;
        }
        .pop-doc-name-link {
          appearance: none !important;
          border: 0 !important;
          background: transparent !important;
          color: #2563eb !important;
          cursor: pointer !important;
          padding: 0 !important;
          text-decoration: none !important;
          box-shadow: none !important;
          font-family: inherit !important;
          font-size: 13.5px !important;
          font-weight: 600 !important;
        }
        .pop-doc-name-link:hover,
        .pop-doc-name-link:focus {
          color: #1d4ed8 !important;
          text-decoration: underline !important;
          outline: none !important;
        }
        @media (max-width: 900px) {
          .pop-doc-row {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          .pop-doc-actions-panel {
            grid-column: 1 / -1 !important;
            min-width: 0 !important;
            justify-content: flex-start !important;
            flex-wrap: wrap !important;
            padding-left: 46px !important;
          }
        }
      `}</style>

      {renderType === "generate" && !embeddedGenerator && (
        <div className="pop-container">
          {hideHeader !== true && poHeader()}
          <div className="pop-content-body">
            {poBody()}
          </div>
        </div>
      )}

      {renderType !== "generate" && (
        <Modal
          show={Boolean(purchaseShowHide)}
          size="xl"
          centered
          onHide={() => handlePurchaseShowHide("")}
          dialogClassName="rt-tools-modal pop-modal-xl"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Body className="p-0">
            <div className="pop-container m-0" style={{ border: "none" }}>
              {hideHeader !== true && poHeader()}
              <div className="pop-content-body">
                {poBody()}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #edf2f7", padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              className="wf-modal-footer-close-btn"
              onClick={() => handlePurchaseShowHide("")}
            >
              <i className="fa fa-times" aria-hidden="true" /> Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      <PrintPurchaseOrder
        genShowHide={genShowHide}
        handleGenShowHide={handleGenShowHide}
        candidateObj={candidateObj}
        workflowTask={workflowTask}
        poAction={poAction}
        allPoTypes={allPoTypes}
        initialPoType={initialPoType}
        embedded={embeddedGenerator}
        onEmbeddedClose={onEmbeddedClose}
        autoSave={autoSave}
        deferSave={deferSave}
        initialGeneratedData={initialGeneratedData}
        onStageGenerated={onStageGenerated}
      ></PrintPurchaseOrder>
    </React.Fragment>
  );
};

export default PurchaseOrderPopup;
